import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, Button, Spinner, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaPlus,
  FaTimesCircle,
} from "react-icons/fa";
import locationpng from "./../assets/deliverylocation.svg";
import homeimg from "./../assets/ion_home-outline.png";
import homeimg2 from "./../assets/ion_home-outline-white.svg";
import apartmentimg from "./../assets/apartment.png";
import apartmentimg2 from "./../assets/tabler_building-skyscraper-white.svg";
import workimg from "./../assets/streamline-ultimate_work-from-home-user-sofa.png";
import workimg2 from "./../assets/streamline-ultimate_work-from-home-user-sofa-white.svg";
import schoolimg from "./../assets/streamline-ultimate-color_study-exam-math.png";
import schoolimg2 from "./../assets/streamline-ultimate-color_study-exam-math-white.png";
import "../Styles/wallet.css";
import "./AddressManagement.css";

const AddressManagement = () => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [primaryAddressId, setPrimaryAddressId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [selectedType, setSelectedType] = useState("Home");
  const [studentInfo, setStudentInfo] = useState([]);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showSchoolProfileModal, setShowSchoolProfileModal] = useState(false);
  const [schoolProfileType, setSchoolProfileType] = useState("student");
  const [profileStudentName, setProfileStudentName] = useState("");
  const [profileStudentClass, setProfileStudentClass] = useState("");
  const [profileStudentSection, setProfileStudentSection] = useState("");
  const [profileTeacherName, setProfileTeacherName] = useState("");
  const [profileTeacherSubjects, setProfileTeacherSubjects] = useState("");
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState(null);

  const [recheckingAddress, setRecheckingAddress] = useState(null); // addressId currently being rechecked
  const hasFetchedRef = useRef(false);
  const fetchAbortControllerRef = useRef(null);

  const isLargeScreen = window.innerWidth >= 768;

  // Address type options
  const addressTypes = [
    { key: "Home", label: "Home", icon: homeimg, icon2: homeimg2 },
    {
      key: "PG",
      label: "PG/ Apartment",
      icon: apartmentimg,
      icon2: apartmentimg2,
    },
    { key: "School", label: "School", icon: schoolimg, icon2: schoolimg2 },
    { key: "Work", label: "Work", icon: workimg, icon2: workimg2 },
  ];

  // Get customer ID from localStorage
  const getCustomerId = () => {
    return JSON.parse(localStorage.getItem("user"))?._id;
  };

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // Clear cache function
  const clearCache = () => {
    const customerId = getCustomerId();
    if (customerId) {
      localStorage.removeItem(`addresses_${customerId}`);
      localStorage.removeItem(`addresses_timestamp_${customerId}`);
    }
    hasFetchedRef.current = false;
  };

  // Re-check if an unserved address now falls inside any hub polygon
  const handleRecheckArea = async (address) => {
    const coords = address.location?.coordinates;
    if (!coords || coords.length < 2) {
      setAlert({ show: true, message: "No coordinates saved for this address. Please edit and re-save it.", type: "warning" });
      return;
    }
    setRecheckingAddress(address._id);
    try {
      const res = await fetch("https://dailydish.in/api/Hub/validate-location", {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ longitude: coords[0], latitude: coords[1] }),
      });
      const data = await res.json();
      if (data.success && data.hubId) {
        // Hub found — update this address in DB
        const customerId = getCustomerId();
        const patchRes = await fetch(
          `https://dailydish.in/api/User/customers/${customerId}/addresses/${address._id}`,
          {
            method: "PATCH",
            headers: getAuthHeaders(),
            body: JSON.stringify({ hubId: data.hubId, hubName: data.hubName || "" }),
          }
        );
        if (patchRes.ok) {
          setAddresses(prev => prev.map(a =>
            a._id === address._id ? { ...a, hubId: data.hubId, hubName: data.hubName || "" } : a
          ));
          clearCache();
          localStorage.removeItem("primaryAddress");
          setAlert({ show: true, message: `Your address is now in the ${data.hubName} delivery zone.`, type: "success" });
        }
      } else {
        setAlert({ show: true, message: "Your address is still outside all delivery zones. We'll notify you when service reaches your area.", type: "info" });
      }
    } catch {
      setAlert({ show: true, message: "Could not check your area right now. Please try again.", type: "danger" });
    } finally {
      setRecheckingAddress(null);
    }
  };

  // Background fetch to update cache
  const fetchAddressesInBackground = useCallback(async (customerId) => {
    try {
      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        },
      );

      if (response.ok) {
        const result = await response.json();
        // The API returns the user object with addresses property
        if (result && Array.isArray(result.addresses)) {
          const addresses = result.addresses || [];
          const primaryId = result.primaryAddress || null;

          // Update cache silently
          localStorage.setItem(
            `addresses_${customerId}`,
            JSON.stringify({ addresses, primaryAddress: primaryId }),
          );
          localStorage.setItem(
            `addresses_timestamp_${customerId}`,
            Date.now().toString(),
          );
        }
      }
    } catch (error) {
      console.error("Background fetch error:", error);
    }
  }, []);

  // OPTIMIZED: Fetch addresses with caching
  const fetchAddresses = useCallback(async () => {
    if (hasFetchedRef.current) return;

    try {
      setLoading(true);
      hasFetchedRef.current = true;

      const customerId = getCustomerId();

      if (!customerId) {
        throw new Error("Customer ID not found. Please login again.");
      }

      // Check localStorage cache first
      const cachedAddresses = localStorage.getItem(`addresses_${customerId}`);
      const cacheTimestamp = localStorage.getItem(
        `addresses_timestamp_${customerId}`,
      );
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      // Use cache if valid and recent
      if (cachedAddresses && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < CACHE_DURATION) {
          const cached = JSON.parse(cachedAddresses);
          setAddresses(cached.addresses || []);
          setPrimaryAddressId(cached.primaryAddress || null);
          setLoading(false);

          // Fetch fresh data in background
          fetchAddressesInBackground(customerId);
          return;
        }
      }

      // Create AbortController for cancellation
      fetchAbortControllerRef.current = new AbortController();

      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses`,
        {
          method: "GET",
          headers: getAuthHeaders(),
          signal: fetchAbortControllerRef.current.signal,
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch addresses");
      }

      const result = await response.json();

      // THE FIX: Based on your data, the API returns the user object
      // with addresses array and primaryAddress field
      let addressesArray = [];
      let primaryId = null;

      // Check if result has addresses directly (user object)
      if (result && Array.isArray(result.addresses)) {
        addressesArray = result.addresses;
        primaryId = result.primaryAddress || null;
      }
      // Fallback: check for success field structure
      else if (result.success && Array.isArray(result.addresses)) {
        addressesArray = result.addresses;
        primaryId = result.primaryAddress || null;
      }

      setAddresses(addressesArray);
      setPrimaryAddressId(primaryId);

      // Cache the results
      localStorage.setItem(
        `addresses_${customerId}`,
        JSON.stringify({
          addresses: addressesArray,
          primaryAddress: primaryId,
        }),
      );
      localStorage.setItem(
        `addresses_timestamp_${customerId}`,
        Date.now().toString(),
      );

      if (addressesArray.length === 0) {
        showAlert("No addresses found. Add your first address!", "info");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching addresses:", error);
        showAlert(error.message || "Failed to load addresses", "danger");
      }
    } finally {
      setLoading(false);
    }
  }, [fetchAddressesInBackground]);

  useEffect(() => {
    // Clear cache on component mount to force fresh fetch
    clearCache();
    fetchAddresses();
    fetchUserProfile();

    return () => {
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, [fetchAddresses]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleAddAddress = () => {
    navigate("/location");
  };

  const handleEditAddress = (address) => {
    navigate("/location", {
      state: {
        editingAddress: address,
      },
    });
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }

    try {
      setActionLoading(true);
      const customerId = getCustomerId();

      if (!customerId) {
        throw new Error("Customer ID not found. Please login again.");
      }

      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses/${addressId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete address");
      }

      const result = await response.json();

      if (result.success) {
        // Update local state immediately
        const updatedAddresses = addresses.filter(
          (addr) => addr._id !== addressId,
        );
        setAddresses(updatedAddresses);

        // If deleted address was primary, clear primary address
        let updatedPrimaryId = primaryAddressId;
        if (primaryAddressId === addressId) {
          updatedPrimaryId = null;
          setPrimaryAddressId(null);
          localStorage.removeItem("primaryAddress");
        }

        // Update cache
        localStorage.setItem(
          `addresses_${customerId}`,
          JSON.stringify({
            addresses: updatedAddresses,
            primaryAddress: updatedPrimaryId,
          }),
        );
        localStorage.setItem(
          `addresses_timestamp_${customerId}`,
          Date.now().toString(),
        );

        showAlert("Address deleted successfully!", "success");
      } else {
        throw new Error(result.message || "Failed to delete address");
      }
    } catch (error) {
      console.error("Error deleting address:", error);
      showAlert(error.message || "Failed to delete address", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetPrimary = async (addressId) => {
    try {
      setActionLoading(true);
      const customerId = getCustomerId();

      if (!customerId) {
        throw new Error("Customer ID not found. Please login again.");
      }

      // Find the address being set as primary
      const primaryAddress = addresses.find((addr) => addr._id === addressId);

      // Update UI immediately
      setPrimaryAddressId(addressId);

      // Store primary address in localStorage
      if (primaryAddress) {
        localStorage.setItem("primaryAddress", JSON.stringify(primaryAddress));
      }

      // Update cache
      localStorage.setItem(
        `addresses_${customerId}`,
        JSON.stringify({
          addresses: addresses,
          primaryAddress: addressId,
        }),
      );
      localStorage.setItem(
        `addresses_timestamp_${customerId}`,
        Date.now().toString(),
      );

      showAlert("Primary address set successfully!", "success");

      // API call in background
      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/addresses/${addressId}/primary`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        // Revert if API fails
        setPrimaryAddressId(null);
        localStorage.removeItem("primaryAddress");
        throw new Error("Failed to set primary address");
      }

      const result = await response.json();

      if (!result.success) {
        // Revert if API fails
        setPrimaryAddressId(null);
        localStorage.removeItem("primaryAddress");
        throw new Error(result.message || "Failed to set primary address");
      }
    } catch (error) {
      console.error("Error setting primary address:", error);
      showAlert(error.message || "Failed to set primary address", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemovePrimary = async () => {
    try {
      setActionLoading(true);
      const customerId = getCustomerId();

      if (!customerId) {
        throw new Error("Customer ID not found. Please login again.");
      }

      // Update UI immediately
      setPrimaryAddressId(null);
      localStorage.removeItem("primaryAddress");

      // Update cache
      localStorage.setItem(
        `addresses_${customerId}`,
        JSON.stringify({
          addresses: addresses,
          primaryAddress: null,
        }),
      );
      localStorage.setItem(
        `addresses_timestamp_${customerId}`,
        Date.now().toString(),
      );

      showAlert("Primary address removed successfully!", "success");

      // API call in background
      const response = await fetch(
        `https://dailydish.in/api/User/customers/${customerId}/primary-address/remove`,
        {
          method: "PATCH",
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove primary address");
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "Failed to remove primary address");
      }
    } catch (error) {
      console.error("Error removing primary address:", error);
      showAlert(error.message || "Failed to remove primary address", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    const customerId = getCustomerId();
    if (!customerId) return;
    try {
      setProfileLoading(true);
      const res = await fetch(
        `https://dailydish.in/api/User/getCustomer/${customerId}/detailed`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const json = await res.json();
      const customer = json.data || {};
      const rawSI = customer.studentInformation;
      setStudentInfo((Array.isArray(rawSI) ? rawSI : []).filter((s) => s.studentName));
      setTeacherInfo(customer.teacherInformation?.teacherName ? customer.teacherInformation : null);
    } catch (err) {
      console.error("Error fetching profile:", err);
    } finally {
      setProfileLoading(false);
    }
  };

  const openSchoolModal = (type = "student") => {
    setSchoolProfileType(type);
    setEditingStudentId(null);
    setProfileStudentName(""); setProfileStudentClass(""); setProfileStudentSection("");
    setProfileTeacherName(""); setProfileTeacherSubjects("");
    setShowSchoolProfileModal(true);
  };

  const startEditStudent = (student) => {
    setSchoolProfileType("student");
    setEditingStudentId(student._id);
    setProfileStudentName(student.studentName || "");
    setProfileStudentClass(student.studentClass || "");
    setProfileStudentSection(student.studentSection || "");
  };

  const startEditTeacher = () => {
    setSchoolProfileType("teacher");
    setEditingStudentId(null);
    setProfileTeacherName(teacherInfo?.teacherName || "");
    setProfileTeacherSubjects(teacherInfo?.subjects || "");
  };

  const handleAddToProfile = async () => {
    const customerId = getCustomerId();
    if (!customerId) return;

    if (schoolProfileType === "student") {
      if (!profileStudentName.trim() || !profileStudentClass.trim() || !profileStudentSection.trim()) return;
      try {
        setIsSavingProfile(true);
        if (editingStudentId) {
          const res = await fetch("https://dailydish.in/api/User/updateStudentInfo", {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({ customerId, studentId: editingStudentId, studentName: profileStudentName.trim(), studentClass: profileStudentClass.trim(), studentSection: profileStudentSection.trim() }),
          });
          const json = await res.json();
          if (json.success) {
            setStudentInfo((json.data || []).filter((s) => s.studentName));
            showAlert("Student updated!", "success");
          }
        } else {
          const res = await fetch("https://dailydish.in/api/User/addStudentInformation", {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({ customerId, userType: "student", studentName: profileStudentName.trim(), studentClass: profileStudentClass.trim(), studentSection: profileStudentSection.trim() }),
          });
          const json = await res.json();
          if (json.success) setStudentInfo((json.data || []).filter((s) => s.studentName));
        }
        setEditingStudentId(null);
        setProfileStudentName(""); setProfileStudentClass(""); setProfileStudentSection("");
      } catch (err) {
        showAlert("Something went wrong.", "danger");
      } finally {
        setIsSavingProfile(false);
      }
    } else {
      if (!profileTeacherName.trim()) return;
      try {
        setIsSavingProfile(true);
        const res = await fetch("https://dailydish.in/api/User/addStudentInformation", {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ customerId, userType: "teacher", teacherName: profileTeacherName.trim(), subjects: profileTeacherSubjects.trim() }),
        });
        const json = await res.json();
        if (json.success) {
          setTeacherInfo({ teacherName: profileTeacherName.trim(), subjects: profileTeacherSubjects.trim() });
          setProfileTeacherName(""); setProfileTeacherSubjects("");
        }
      } catch (err) {
        showAlert("Something went wrong.", "danger");
      } finally {
        setIsSavingProfile(false);
      }
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Delete this student?")) return;
    const customerId = getCustomerId();
    try {
      setIsSavingProfile(true);
      const res = await fetch("https://dailydish.in/api/User/deleteStudentInfo", {
        method: "DELETE",
        headers: getAuthHeaders(),
        body: JSON.stringify({ customerId, studentId }),
      });
      const json = await res.json();
      if (json.success) {
        setStudentInfo((json.data || []).filter((s) => s.studentName));
        if (editingStudentId === studentId) {
          setEditingStudentId(null);
          setProfileStudentName(""); setProfileStudentClass(""); setProfileStudentSection("");
        }
        showAlert("Student removed.", "success");
      }
    } catch (err) {
      showAlert("Something went wrong.", "danger");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleDeleteTeacher = async () => {
    if (!window.confirm("Delete teacher details?")) return;
    const customerId = getCustomerId();
    try {
      setIsSavingProfile(true);
      const res = await fetch("https://dailydish.in/api/User/deleteTeacherInfo", {
        method: "DELETE",
        headers: getAuthHeaders(),
        body: JSON.stringify({ customerId }),
      });
      const json = await res.json();
      if (json.success) {
        setTeacherInfo(null);
        if (schoolProfileType === "teacher") {
          setProfileTeacherName(""); setProfileTeacherSubjects("");
        }
        showAlert("Teacher removed.", "success");
      }
    } catch (err) {
      showAlert("Something went wrong.", "danger");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 3000);
  };

  // Get the primary name for each address type
  const getPrimaryName = (address) => {
    switch (address.addressType) {
      case "Home":
        return address.homeName || address.houseName;
      case "PG":
        return address.apartmentName || address.houseName;
      case "School":
        return address.schoolName || address.houseName;
      case "Work":
        return address.companyName || address.houseName;
      default:
        return address.houseName;
    }
  };

  const formatAddressDetails = (address) => {
    let details = [];

    if (address.addressType === "Home") {
      if (address.landmark) details.push(`Landmark: ${address.landmark}`);
      if (address.floor) details.push(`Floor: ${address.floor}`);
    } else if (address.addressType === "PG") {
      if (address.towerBlock) details.push(`Block: ${address.towerBlock}`);
      if (address.flat) details.push(`Flat: ${address.flat}`);
      if (address.floor) details.push(`Floor: ${address.floor}`);
    } else if (address.addressType === "School") {
      if (address.studentInformation?.studentName)
        details.push(`Student: ${address.studentInformation.studentName}`);
      if (address.studentInformation?.studentClass)
        details.push(`Class: ${address.studentInformation.studentClass}`);
      if (address.studentInformation?.studentSection)
        details.push(`Section: ${address.studentInformation.studentSection}`);
    } else if (address.addressType === "Work") {
      if (address.floorNo) details.push(`Floor: ${address.floorNo}`);
      if (address.buildingName)
        details.push(`Building Name: ${address.buildingName} `);
    }

    return details;
  };

  // Filter addresses by selected type
  const filteredAddresses = addresses.filter(
    (addr) => addr.addressType === selectedType,
  );

  // Check if an address is primary
  const isPrimaryAddress = (addressId) => {
    return primaryAddressId === addressId;
  };

  return (
    <div className="ban-containersd">
      <div className="checkoutcontainer">
        <div className="mobile-banner-updated">
          <div className="screen-checkout mb-2">
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                onClick={handleGoBack}
                className="cursor-pointer"
              >
                <path
                  d="M11.7375 19.5002L19.0875 26.8502C19.3875 27.1502 19.5315 27.5002 19.5195 27.9002C19.5075 28.3002 19.351 28.6502 19.05 28.9502C18.75 29.2252 18.4 29.3692 18 29.3822C17.6 29.3952 17.25 29.2512 16.95 28.9502L7.05001 19.0502C6.90001 18.9002 6.79351 18.7377 6.73051 18.5627C6.66751 18.3877 6.63701 18.2002 6.63901 18.0002C6.64101 17.8002 6.67251 17.6127 6.73351 17.4377C6.79451 17.2627 6.90051 17.1002 7.05151 16.9502L16.9515 7.05019C17.2265 6.77519 17.5705 6.6377 17.9835 6.6377C18.3965 6.6377 18.7525 6.77519 19.0515 7.05019C19.3515 7.35019 19.5015 7.7067 19.5015 8.1197C19.5015 8.5327 19.3515 8.8887 19.0515 9.1877L11.7375 16.5002H28.5C28.925 16.5002 29.2815 16.6442 29.5695 16.9322C29.8575 17.2202 30.001 17.5762 30 18.0002C29.999 18.4242 29.855 18.7807 29.568 19.0697C29.281 19.3587 28.925 19.5022 28.5 19.5002H11.7375Z"
                  fill="#FAFAFA"
                />
              </svg>
            </div>
            <h3 className="checkout-title">My Addresses</h3>
          </div>
        </div>
      </div>

      <div
        className="mobile-banner-updated container"
        style={{ backgroundColor: "white" }}
      >
        {/* Alert */}
        {alert.show && (
          <Alert variant={alert.type} className="mb-3">
            {alert.message}
          </Alert>
        )}

        {/* Add Address Card */}
        <div className="wallet-card mb-4">
          <div className="card-content">
            <div className="header-section">
              <div className="wallet-icon">
                <FaMapMarkerAlt size={40} color="#6B8E23" />
              </div>
              <div className="card-title">
                <div className="title-textP">Manage Addresses</div>
              </div>
            </div>
            <div className="balance-section">
              <div className="balance-info">
                <div className="balance-label">
                  <div className="label-text">Saved Addresses</div>
                </div>
                <div className="balance-amount">
                  <div className="amount-text">
                    {addresses.length} Addresses
                  </div>
                </div>
              </div>
              <div className="transaction-summary" onClick={handleAddAddress}>
                <div className="summary-content">
                  <div className="summary-label">
                    <div className="summary-text">Add New Address</div>
                  </div>
                </div>
                <FaPlus size={16} color="#6B8E23" />
              </div>
            </div>
          </div>
        </div>

        {/* Student & Teacher Summary Card */}
        <div
          className="mb-4"
          style={{
            borderRadius: "12px",
            border: "1px solid #e8f5e9",
            background: "linear-gradient(135deg, #f8fff8 0%, #f0f9f0 100%)",
            padding: "14px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "38px", height: "38px", borderRadius: "10px",
                background: "#6B8E23", display: "flex", alignItems: "center",
                justifyContent: "center", flexShrink: 0,
              }}
            >
              <span style={{ fontSize: "18px" }}> <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
    <path d="M2 17l10 5 10-5"/>
    <path d="M2 12l10 5 10-5"/>
  </svg>
              </span>
            </div>
            <div>
              <div style={{ fontWeight: "600", fontFamily: "Inter", fontSize: "14px", color: "#2c2c2c" }}>
                Saved Student / Teacher profiles
              </div>
              <div style={{ fontSize: "12px", color: "#666", fontFamily: "Inter", marginTop: "2px" }}>
                {profileLoading ? (
                  "Loading..."
                ) : studentInfo.length === 0 && !teacherInfo ? (
                  "No student or teacher saved yet"
                ) : (
                  [
                    studentInfo.length > 0 && `${studentInfo.length} student${studentInfo.length > 1 ? "s" : ""}`,
                    teacherInfo && "1 teacher",
                  ]
                    .filter(Boolean)
                    .join(" · ")
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => openSchoolModal()}
            style={{
              background: "#6B8E23", color: "#fff", border: "none",
              borderRadius: "8px", padding: "7px 16px", fontSize: "13px",
              fontFamily: "Inter", fontWeight: "500", cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {studentInfo.length === 0 && !teacherInfo ? "Add" : "View"}
          </button>
        </div>

        {/* Primary Address Actions */}
        {primaryAddressId && (
          <div
            className="mb-3 p-3"
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small className="text-muted">Current Primary Address</small>
                <div className="d-flex align-items-center mt-1">
                  <FaCheckCircle className="text-success me-2" />
                  <span style={{ fontWeight: "500" }}>
                    {(() => {
                      const primaryAddress = addresses.find(
                        (addr) => addr._id === primaryAddressId,
                      );
                      if (!primaryAddress) return "Primary Address";

                      switch (primaryAddress.addressType) {
                        case "Home":
                          return (
                            primaryAddress.homeName ||
                            primaryAddress.houseName ||
                            "Home Address"
                          );
                        case "PG":
                          return (
                            primaryAddress.apartmentName ||
                            primaryAddress.houseName ||
                            "Apartment Address"
                          );
                        case "School":
                          return (
                            primaryAddress.schoolName ||
                            primaryAddress.houseName ||
                            "School Address"
                          );
                        case "Work":
                          return (
                            primaryAddress.companyName ||
                            primaryAddress.houseName ||
                            "Work Address"
                          );
                        default:
                          return primaryAddress.houseName || "Primary Address";
                      }
                    })()}
                  </span>
                </div>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemovePrimary}
                disabled={actionLoading}
              >
                <FaTimesCircle className="me-1" />
                Remove Primary
              </Button>
            </div>
          </div>
        )}

        {/* Address Type Selection */}
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "grid",
              display: window.innerWidth <= 768 ? "flex" : "grid",
              flexWrap: window.innerWidth <= 768 ? "wrap" : "nowrap",
              justifyContent:
                window.innerWidth <= 768 ? "flex-start" : "stretch",
              alignItems: "stretch",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
            }}
          >
            {addressTypes.map((type) => (
              <button
                key={type.key}
                type="button"
                onClick={() => setSelectedType(type.key)}
                style={{
                  width: window.innerWidth <= 768 ? "auto" : "100%",
                  height: isLargeScreen ? "55px" : "43px",
                  padding: "8px",
                  borderRadius: "12px",
                  border: "1.2px solid #F5DEB3",
                  backgroundColor:
                    selectedType === type.key ? "#6B8E23" : "#FFF8DC",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  display: "inline-flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  opacity: 1,
                }}
              >
                <img
                  src={selectedType === type.key ? type.icon2 : type.icon}
                  alt={type.label}
                  style={{
                    width: isLargeScreen ? "24px" : "16px",
                    height: isLargeScreen ? "24px" : "16px",
                    objectFit: "contain",
                  }}
                />
                <span
                  style={{
                    fontSize:
                      window.innerWidth <= 360
                        ? "10px"
                        : window.innerWidth <= 768
                          ? "11px"
                          : "14px",
                    fontWeight: "500",
                    fontFamily: "Inter",
                    color: selectedType === type.key ? "#fff" : "#000",
                    textAlign: "center",
                    lineHeight: "1.3",
                    whiteSpace: window.innerWidth <= 768 ? "nowrap" : "normal",
                    wordBreak:
                      window.innerWidth <= 768 ? "normal" : "break-word",
                  }}
                >
                  {type.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Address List */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" style={{ color: "#6B8E23" }} />
            <p className="mt-2">Loading addresses...</p>
          </div>
        ) : addresses.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <FaMapMarkerAlt size={48} color="#6B8E23" className="mb-3" />
              <h5>No Addresses Saved</h5>
              <p className="text-muted">
                Add your first delivery address to get started
              </p>
              <Button
                variant="outline-success"
                onClick={handleAddAddress}
                className="no-hover-btn"
              >
                <FaPlus className="me-2" />
                Add Your First Address
              </Button>
            </Card.Body>
          </Card>
        ) : filteredAddresses.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <FaMapMarkerAlt size={48} color="#6B8E23" className="mb-3" />
              <h5>
                No {addressTypes.find((t) => t.key === selectedType)?.label}{" "}
                Addresses
              </h5>
              <p className="text-muted">
                No {selectedType.toLowerCase()} addresses saved yet
              </p>
              <Button
                variant="success"
                onClick={handleAddAddress}
                style={{ backgroundColor: "#6B8E23", border: "none" }}
              >
                <FaPlus className="me-2" />
                Add {
                  addressTypes.find((t) => t.key === selectedType)?.label
                }{" "}
                Address
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <div className="address-list">
            {filteredAddresses.map((address) => (
              <Card
                key={address._id}
                className={`mb-3 ${
                  isPrimaryAddress(address._id) ? "border-success" : ""
                }`}
                style={{
                  border: isPrimaryAddress(address._id)
                    ? "2px solid #6B8E23"
                    : "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: isPrimaryAddress(address._id)
                    ? "#f8fff8"
                    : "white",
                }}
              >
                <Card.Body>
                  {/* Address Header */}
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex align-items-start flex-grow-1">
                      <div style={{ marginRight: "12px" }}>
                        <img
                          src={locationpng}
                          alt=""
                          style={{ width: "20px", height: "20px" }}
                        />
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <h6
                            className="mb-0"
                            style={{
                              color: "#2c2c2c",
                              fontFamily: "Inter",
                              fontWeight: "600",
                              fontSize: "16px",
                            }}
                          >
                            {getPrimaryName(address)}
                          </h6>
                          {isPrimaryAddress(address._id) && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "10px",
                                color: "#6B8E23",
                                fontWeight: "500",
                                backgroundColor: "#f0f9f0",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              PRIMARY
                            </span>
                          )}
                          {!address.hubId && (
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "10px",
                                color: "#842029",
                                fontWeight: "600",
                                backgroundColor: "#f8d7da",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                border: "1px solid #f5c2c7",
                              }}
                            >
                              Outside service area
                            </span>
                          )}
                        </div>
                        <p
                          style={{
                            margin: 0,
                            color: "#666",
                            fontSize: "12px",
                            fontFamily: "Inter",
                            lineHeight: "1.4",
                          }}
                        >
                          {address.fullAddress}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Address Details */}
                  <div className="address-details mb-3">
                    {formatAddressDetails(address).map((detail, index) => (
                      <div key={index} className="address-detail-item">
                        <small
                          className="text-muted"
                          style={{ fontFamily: "Inter" }}
                        >
                          {detail}
                        </small>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex gap-2 flex-wrap">
                      {!isPrimaryAddress(address._id) && (
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleSetPrimary(address._id)}
                          disabled={actionLoading}
                          className="primary-btn"
                        >
                          <FaCheckCircle className="me-1" />
                          Set as Primary
                        </Button>
                      )}
                      {!address.hubId && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleRecheckArea(address)}
                          disabled={recheckingAddress === address._id}
                          style={{ fontSize: 12 }}
                        >
                          {recheckingAddress === address._id ? (
                            <><Spinner animation="border" size="sm" className="me-1" />Checking...</>
                          ) : (
                            "Re-check my area"
                          )}
                        </Button>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditAddress(address)}
                        disabled={actionLoading}
                        className="edit-btn"
                      >
                        <FaEdit className="me-1" />
                        Edit
                      </Button>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteAddress(address._id)}
                        disabled={actionLoading}
                        className="delete-btn"
                      >
                        <FaTrash className="me-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── School Profile Bottom-Sheet Modal ── */}
      {showSchoolProfileModal && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zIndex: 4000,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowSchoolProfileModal(false); }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "20px 20px 0 0",
              width: "100%",
              maxWidth: "480px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 -8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #6B8E23 0%, #4a6e12 100%)",
                borderRadius: "20px 20px 0 0",
                padding: "24px 20px 20px",
                textAlign: "center",
                color: "#fff",
              }}
            >
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>🎓</div>
              <h3 style={{ fontSize: "18px", fontWeight: "700", fontFamily: "Inter", margin: "0 0 6px" }}>
                School Profiles
              </h3>
              <p style={{ fontSize: "13px", margin: 0, opacity: 0.85, lineHeight: "1.4", fontFamily: "Inter" }}>
                Manage student and teacher details for your school deliveries.
              </p>
            </div>

            <div style={{ padding: "20px" }}>
              {/* Student / Teacher toggle */}
              <div
                style={{
                  display: "flex", backgroundColor: "#f0f0f0",
                  borderRadius: "12px", padding: "4px", marginBottom: "16px",
                }}
              >
                {["student", "teacher"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setSchoolProfileType(t);
                      setEditingStudentId(null);
                      setProfileStudentName(""); setProfileStudentClass(""); setProfileStudentSection("");
                      setProfileTeacherName(""); setProfileTeacherSubjects("");
                    }}
                    style={{
                      flex: 1, padding: "10px", border: "none", borderRadius: "10px",
                      backgroundColor: schoolProfileType === t ? "#6B8E23" : "transparent",
                      color: schoolProfileType === t ? "#fff" : "#555",
                      fontWeight: "600", fontSize: "14px", fontFamily: "Inter",
                      cursor: "pointer", transition: "all 0.2s",
                    }}
                  >
                    {t === "student" ? "Student" : "Teacher"}
                  </button>
                ))}
              </div>

              {/* Saved profiles list */}
              {(studentInfo.length > 0 || teacherInfo) && (
                <div style={{ marginBottom: "16px" }}>
                  {studentInfo.map((s) => (
                    <div
                      key={s._id}
                      style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        backgroundColor: editingStudentId === s._id ? "#e8f5e9" : "#f0f7e6",
                        border: editingStudentId === s._id ? "1.5px solid #6B8E23" : "1px solid #c5dfa0",
                        borderRadius: "10px", padding: "10px 14px", marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>🎒</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#2c2c2c", fontFamily: "Inter" }}>
                          {s.studentName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b6b6b", fontFamily: "Inter" }}>
                          Class {s.studentClass} · Sec {s.studentSection}{s.block ? ` · ${s.block}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => startEditStudent(s)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#4a90d9", padding: "4px" }}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(s._id)}
                        disabled={isSavingProfile}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e05c5c", padding: "4px" }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  ))}
                  {teacherInfo && (
                    <div
                      style={{
                        display: "flex", alignItems: "center", gap: "10px",
                        backgroundColor: "#fffdf4", border: "1px solid #fce8a0",
                        borderRadius: "10px", padding: "10px 14px", marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>📚</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#2c2c2c", fontFamily: "Inter" }}>
                          {teacherInfo.teacherName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#6b6b6b", fontFamily: "Inter" }}>
                          {[teacherInfo.subjects, teacherInfo.block && `Block ${teacherInfo.block}`].filter(Boolean).join(" · ") || "Teacher"}
                        </div>
                      </div>
                      <button
                        onClick={startEditTeacher}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#4a90d9", padding: "4px" }}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={handleDeleteTeacher}
                        disabled={isSavingProfile}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#e05c5c", padding: "4px" }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Form fields */}
              {schoolProfileType === "student" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <input
                    type="text"
                    value={profileStudentName}
                    onChange={(e) => setProfileStudentName(e.target.value)}
                    placeholder="Student's Full Name *"
                    style={{ width: "100%", height: "46px", borderRadius: "12px", padding: "8px 16px", border: "0.4px solid #6B8E23", background: "#FAFAFA", fontSize: "14px", fontFamily: "Inter", boxSizing: "border-box", outline: "none" }}
                  />
                  <div style={{ display: "flex", gap: "10px" }}>
                    <input
                      type="text"
                      value={profileStudentClass}
                      onChange={(e) => setProfileStudentClass(e.target.value)}
                      placeholder="Class *"
                      style={{ flex: 1, minWidth: 0, height: "46px", borderRadius: "12px", padding: "8px 16px", border: "0.4px solid #6B8E23", background: "#FAFAFA", fontSize: "14px", fontFamily: "Inter", outline: "none", boxSizing: "border-box" }}
                    />
                    <input
                      type="text"
                      value={profileStudentSection}
                      onChange={(e) => setProfileStudentSection(e.target.value)}
                      placeholder="Section *"
                      style={{ flex: 1, minWidth: 0, height: "46px", borderRadius: "12px", padding: "8px 16px", border: "0.4px solid #6B8E23", background: "#FAFAFA", fontSize: "14px", fontFamily: "Inter", outline: "none", boxSizing: "border-box" }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <input
                    type="text"
                    value={profileTeacherName}
                    onChange={(e) => setProfileTeacherName(e.target.value)}
                    placeholder="Teacher's Full Name *"
                    style={{ width: "100%", height: "46px", borderRadius: "12px", padding: "8px 16px", border: "0.4px solid #6B8E23", background: "#FAFAFA", fontSize: "14px", fontFamily: "Inter", boxSizing: "border-box", outline: "none" }}
                  />
                  <input
                    type="text"
                    value={profileTeacherSubjects}
                    onChange={(e) => setProfileTeacherSubjects(e.target.value)}
                    placeholder="Subject (e.g. Maths, Science)"
                    style={{ width: "100%", height: "46px", borderRadius: "12px", padding: "8px 16px", border: "0.4px solid #6B8E23", background: "#FAFAFA", fontSize: "14px", fontFamily: "Inter", boxSizing: "border-box", outline: "none" }}
                  />
                </div>
              )}

              {/* Add / Update button */}
              <button
                type="button"
                onClick={handleAddToProfile}
                disabled={
                  isSavingProfile ||
                  (schoolProfileType === "student"
                    ? !profileStudentName.trim() || !profileStudentClass.trim() || !profileStudentSection.trim()
                    : !profileTeacherName.trim())
                }
                style={{
                  width: "100%", marginTop: "14px", padding: "12px",
                  border: "1.5px solid #6B8E23", borderRadius: "12px",
                  backgroundColor: "transparent", color: "#6B8E23",
                  fontWeight: "600", fontSize: "14px", fontFamily: "Inter",
                  cursor: isSavingProfile ? "not-allowed" : "pointer",
                  opacity: isSavingProfile || (schoolProfileType === "student" ? !profileStudentName.trim() || !profileStudentClass.trim() || !profileStudentSection.trim() : !profileTeacherName.trim()) ? 0.5 : 1,
                  transition: "all 0.2s",
                }}
              >
                {isSavingProfile ? "Saving..." : editingStudentId ? "Update Student" : `+ ${schoolProfileType === "student" ? "Add Student" : teacherInfo ? "Update Teacher" : "Add Teacher"}`}
              </button>

              {/* Done button */}
              <button
                type="button"
                onClick={() => setShowSchoolProfileModal(false)}
                style={{
                  width: "100%", marginTop: "10px", padding: "14px",
                  border: "none", borderRadius: "12px",
                  backgroundColor: "#E6B800", color: "#000",
                  fontWeight: "700", fontSize: "15px", fontFamily: "Inter",
                  cursor: "pointer", transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#FFD700"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#E6B800"; }}
              >
                Done ✓
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mobile-banner"></div>
    </div>
  );
};

export default AddressManagement;
